/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observable, reaction } from 'mobx';
import * as React from 'react';

import { INosPhonePickerData } from './nos-phone-picker.data';
import { INosPhonePickerProps } from './nos-phone-picker.props.autogenerated';
import SignUpInputPhonePicker from './components/sign-up-input-phone';
import { IGetAvailableCountriesData } from '../../actions/get-available-countries.action';
import { InputType } from '../nos-business-sign-up';

/**
 * Interface for IPhonePickerExtendedProps.
 */
export interface IPhonePickerExtendedProps extends INosPhonePickerProps<INosPhonePickerData> {
    updatePhone(phone: string): void;
    updatePhoneCode(newSelectValue: string): void;
    selectedValue: string;
    inputValue: string;
    className: string;
    isSubmittedPressed: boolean;
}

/**
 * Interface for IPhonePickerState.
 */
export interface IPhonePickerState {
    phoneId?: string;
}

/**
 * Interface for IPhonePickerViewProps.
 */
export interface IPhonePickerViewProps {
    className: string;
    PhonePicker: IModuleProps;
    phoneComponent: any;
}

/**
 *
 * Address component.
 * @extends {React.Component<INosPhonePickerProps<INosPhonePickerData>, IPhonePickerState>}
 */
class NosPhonePicker extends React.Component<IPhonePickerExtendedProps, IPhonePickerState> {
    @observable private availablePhones?: IGetAvailableCountriesData[];

    public constructor(props: IPhonePickerExtendedProps) {
        super(props);
    }

    public componentDidMount(): void {
        // Initializing data props
        this._dataInitialize(this.props);
    }

    public shouldComponentUpdate(nextProps: IPhonePickerExtendedProps, nextState: IPhonePickerState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const { renderView, config, selectedValue } = this.props;

        const viewProps = {
            ...this.props,
            className: config.className,
            PhonePicker: {
                moduleProps: this.props,
                className: classnames('ms-business-account-address', config.className)
            },
            phoneComponent: this._initializeDropdown(this.availablePhones ?? [], selectedValue)
        };

        return renderView(viewProps) as React.ReactElement;
    }

    private readonly _initializeDropdown = (availablePhones: IGetAvailableCountriesData[], selectedValue: string) => {
        return (
            <SignUpInputPhonePicker
                type={InputType.Phone}
                value={this.props.inputValue}
                className={this.props.className ?? ''}
                selectedValue={selectedValue}
                isSubmittedPressed={this.props.isSubmittedPressed}
                onChangeInput={(event: React.ChangeEvent<HTMLSelectElement>, inputValue: string, type: string) =>
                    this._onInputChange(event, inputValue, type)
                }
            />
        );
    };

    private readonly _onInputChange = (event: React.ChangeEvent<HTMLSelectElement>, inputValue: string, type: string) => {
        if (type === 'select') {
            this.props.updatePhoneCode(event.target.value);
            // }
        } else {
            this.props.updatePhone(event.target.value);
        }
    };

    /**
     * Method data initialization.
     * @param props -The account management props.
     */
    private readonly _dataInitialize = (props: IPhonePickerExtendedProps): void => {
        const { data } = props;

        reaction(
            () => data.availableCountries.result,
            () => {
                this.availablePhones = data.availableCountries.result ?? [];
            }
        );
    };
}

export default NosPhonePicker;
